import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, SearchField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import type { EmployeeStatusFilter } from '@/api/handlers/resources/salaries/requests';
import { SuccessNotice } from '@/components/successNotice';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    AccessNotice,
    EmployeeFormDrawer,
    EmployeeTable,
    PayrollRunDialog,
    PayrollRunList,
    RegisterSummary,
    RunDetailDrawer,
    TieOutBar,
} from './components';
import { EMPLOYEE_STATUS_FILTERS, savedMessageKey } from './salariesMeta';
import type { UseSalariesPageResult } from './useSalariesPage';

type SalariesPageViewProps = {
    page: UseSalariesPageResult;
    /** `null` while the register is in flight — labels stay, values skeleton. */
    figures: UseSalariesPageResult['figures'];
    isLoading: boolean;
};

/**
 * Render-only Salaries surface. The shell supplies the page title, so there is
 * none here and no page-root padding.
 *
 * Order follows the general-to-specific hierarchy: the three summary figures,
 * then the accrued-vs-ledger comparison (a first-class element, not a
 * footnote), then the controls, then the register itself, then the payroll runs
 * it has produced. The overlays are rendered last and are mutually exclusive.
 *
 * A session that may not read payroll gets the notice **instead of** all of it —
 * not a disabled version of it. There is no branch-scoped view of an org-wide
 * register to degrade to, so showing empty cards and a dead toolbar would be
 * pretending a screen exists here for them.
 */
export const SalariesPageView: React.FC<SalariesPageViewProps> = ({
    page,
    figures,
    isLoading,
}) => {
    const { t } = useTranslation('pages');
    const { access, overlay } = page;

    if (access.kind !== 'full') {
        return (
            <section
                aria-label={t('salaries.title')}
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('salaries-section')}
            >
                <AccessNotice access={access} />
            </section>
        );
    }

    return (
        <section
            aria-label={t('salaries.title')}
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('salaries-section')}
        >
            {/* Above the summary, because it is about the press that just
                happened rather than about the register's standing figures. No
                link: the reader is already looking at the list they joined. */}
            {page.saved ? (
                <SuccessNotice
                    dismissLabel={t('salaries.result.dismiss')}
                    message={t(savedMessageKey(page.saved), {
                        name: page.saved.name,
                    })}
                    onDismiss={page.onDismissSaved}
                    testId="salaries-result"
                />
            ) : null}

            <RegisterSummary figures={figures} rates={page.rates} />

            <TieOutBar tieOut={page.tieOut} />

            <div
                className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
                {...getNodeTestIdProp('salaries-toolbar')}
            >
                <div className="flex flex-wrap items-center gap-3">
                    <SearchField
                        aria-label={t('salaries.searchAria')}
                        className="w-full sm:max-w-xs"
                        isDisabled={isLoading}
                        onChange={page.onSearchChange}
                        value={page.search}
                        {...getNodeTestIdProp('salaries-search')}
                    >
                        <SearchField.Group>
                            <SearchField.SearchIcon />
                            <SearchField.Input
                                placeholder={t('salaries.searchPlaceholder')}
                            />
                            <SearchField.ClearButton />
                        </SearchField.Group>
                    </SearchField>

                    <Segment
                        aria-label={t('salaries.filters.aria')}
                        isDisabled={isLoading}
                        onSelectionChange={(key) =>
                            page.onStatusChange(key as EmployeeStatusFilter)
                        }
                        selectedKey={page.status}
                        size="sm"
                        {...getNodeTestIdProp('salaries-status-filter')}
                    >
                        {EMPLOYEE_STATUS_FILTERS.map((filter) => (
                            <Segment.Item
                                id={filter.id}
                                key={filter.id}
                                {...getNodeTestIdProp(
                                    `salaries-filter-${filter.id}`,
                                )}
                            >
                                <Segment.Separator />
                                {t(filter.labelKey)}
                            </Segment.Item>
                        ))}
                    </Segment>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        isDisabled={isLoading}
                        onPress={page.onOpenRun}
                        variant="outline"
                        {...getNodeTestIdProp('salaries-run-payroll')}
                    >
                        <Icon aria-hidden icon="gravity-ui:calendar" />
                        {t('salaries.actions.runPayroll')}
                    </Button>
                    <Button
                        isDisabled={isLoading}
                        onPress={page.onOpenCreate}
                        variant="primary"
                        {...getNodeTestIdProp('salaries-add')}
                    >
                        <Icon aria-hidden icon="gravity-ui:plus" />
                        {t('salaries.actions.newEmployee')}
                    </Button>
                </div>
            </div>

            <EmployeeTable
                ariaLabel={t('salaries.tableAria')}
                emptyState={page.emptyState}
                isLoading={isLoading}
                onEdit={page.onOpenEdit}
                rows={page.rows}
            />

            <div className="flex flex-col gap-2 pt-2">
                <h2 className="text-sm font-semibold text-foreground">
                    {t('salaries.runs.title')}
                </h2>
                <PayrollRunList
                    isLoading={isLoading || page.isRunsLoading}
                    onOpenRun={page.onOpenRunDetail}
                    rows={page.runRows}
                />
            </div>

            <p className="text-xs text-muted">{t('salaries.footerNote')}</p>

            {overlay.kind === 'FORM' ? (
                <EmployeeFormDrawer
                    employee={overlay.employee}
                    isOpen
                    // A fresh mount per open, so a second "New employee" never
                    // inherits the previous draft (the account drawer's rule).
                    key={`${overlay.employee?.id ?? 'new'}-${overlay.instanceId}`}
                    onClose={page.onCloseOverlay}
                    onSaved={page.onSaved}
                    rates={page.rates}
                />
            ) : null}

            {overlay.kind === 'RUN' ? (
                <PayrollRunDialog
                    isOpen
                    locale={page.locale}
                    onClose={page.onCloseOverlay}
                    onOpenExistingRun={page.onOpenRunDetail}
                />
            ) : null}

            <RunDetailDrawer
                journalEntryId={
                    overlay.kind === 'RUN_DETAIL'
                        ? overlay.journalEntryId
                        : null
                }
                locale={page.locale}
                onClose={page.onCloseOverlay}
            />
        </section>
    );
};
